import type { MiningEntry } from './mining-types';

export interface MiningTotals {
  entryCount: number;
  materialCount: number;
  systemCount: number;
  activeDays: number;
  quantity: number;
  volume: number | null;
  unknownVolumeCount: number;
  firstDate: string | null;
  lastDate: string | null;
}

export interface MiningGroup {
  id: string;
  name: string;
  entries: MiningEntry[];
  totals: MiningTotals;
  share: number | null;
  systems: Array<{ id: number; name: string }>;
  materials: Array<{ id: number; name: string }>;
}

// Add decimal representations exactly before converting the final total back to
// a number. Repeated fractional ore volumes should not introduce display noise.
function decimalSum(values: number[]): number {
  let total = 0n;
  let scale = 0;
  for (const value of values) {
    const [mantissa, exponent = '0'] = value.toString().toLowerCase().split('e');
    const decimals = mantissa.split('.')[1]?.length ?? 0;
    const valueScale = decimals - Number(exponent);
    let coefficient = BigInt(mantissa.replace('.', ''));
    if (valueScale > scale) {
      total *= 10n ** BigInt(valueScale - scale);
      scale = valueScale;
    } else {
      coefficient *= 10n ** BigInt(scale - valueScale);
    }
    total += coefficient;
  }
  return Number(`${total}e-${scale}`);
}

function compareNames(a: { id: number; name: string }, b: { id: number; name: string }): number {
  return a.name.localeCompare(b.name, 'es') || a.id - b.id;
}

function identities(entries: MiningEntry[], mode: 'material' | 'system'): Array<{ id: number; name: string }> {
  const values = new Map<number, string>();
  for (const entry of entries) {
    const id = mode === 'material' ? entry.typeId : entry.solarSystemId;
    const name = mode === 'material' ? entry.typeName : entry.solarSystemName;
    const previous = values.get(id);
    if (previous === undefined || name.localeCompare(previous, 'es') < 0) values.set(id, name);
  }
  return Array.from(values, ([id, name]) => ({ id, name })).sort(compareNames);
}

export function miningTotals(entries: MiningEntry[]): MiningTotals {
  const dates = Array.from(new Set(entries.map(entry => entry.date))).sort();
  const volumes = entries.flatMap(entry => entry.volume === null ? [] : [entry.volume]);
  return {
    entryCount: entries.length,
    materialCount: new Set(entries.map(entry => entry.typeId)).size,
    systemCount: new Set(entries.map(entry => entry.solarSystemId)).size,
    activeDays: dates.length,
    quantity: decimalSum(entries.map(entry => entry.quantity)),
    volume: volumes.length ? decimalSum(volumes) : null,
    unknownVolumeCount: entries.length - volumes.length,
    firstDate: dates[0] ?? null,
    lastDate: dates.at(-1) ?? null,
  };
}

export function groupMining(entries: MiningEntry[], mode: 'material' | 'system'): MiningGroup[] {
  const grouped = new Map<number, MiningEntry[]>();
  for (const entry of entries) {
    const id = mode === 'material' ? entry.typeId : entry.solarSystemId;
    const group = grouped.get(id);
    if (group) group.push(entry);
    else grouped.set(id, [entry]);
  }
  const totalVolume = miningTotals(entries).volume;
  return Array.from(grouped, ([id, groupEntries]) => {
    const totals = miningTotals(groupEntries);
    const systems = identities(groupEntries, 'system');
    const materials = identities(groupEntries, 'material');
    return {
      id: `${mode}-${id}`,
      name: (mode === 'material' ? materials : systems)[0].name,
      entries: [...groupEntries].sort((a, b) => b.date.localeCompare(a.date)),
      totals,
      share: totalVolume !== null && totalVolume > 0 && totals.volume !== null
        ? totals.volume / totalVolume * 100
        : null,
      systems,
      materials,
    };
  }).sort((a, b) => {
    if (a.totals.volume === null && b.totals.volume !== null) return 1;
    if (a.totals.volume !== null && b.totals.volume === null) return -1;
    return (b.totals.volume ?? 0) - (a.totals.volume ?? 0)
      || a.name.localeCompare(b.name, 'es')
      || a.id.localeCompare(b.id, 'en', { numeric: true });
  });
}

export interface MiningDaySegment { id: number; name: string; volume: number }
export interface MiningDay { date: string; segments: MiningDaySegment[]; total: number }
export interface MiningDailyVolume {
  days: MiningDay[];
  materials: Array<{ id: number; name: string }>;
  unknownVolumeCount: number;
}

export const OTHER_MATERIALS = 0;

/** m³ per UTC day and material family, retaining idle days on the time axis. */
export function miningDailyVolume(entries: MiningEntry[], today: string, dayCount = 30, top = 5): MiningDailyVolume {
  const dates = Array.from({ length: dayCount }, (_, index) => {
    const date = new Date(`${today}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() - (dayCount - 1 - index));
    return date.toISOString().slice(0, 10);
  });
  const inWindow = entries.filter(entry => entry.date >= dates[0] && entry.date <= today);
  const known = inWindow.filter(entry => entry.volume !== null && entry.volume > 0);
  // EVE uses names such as Kernite II-Grade and Veldspar 0-Grade/Isotope.
  // These variants and shorthand Kernite-II share a stack, legend entry and rank.
  // Use the smallest observed type ID as the family's ID, independent of input order.
  const familyName = (entry: MiningEntry) => entry.typeName.replace(/(?:-[IVXLCDM]+| (?:[IVXLCDM]+|\d+)-Grade| Isotope)$/, '');
  const familyIds = new Map<string, number>();
  for (const entry of known) {
    const name = familyName(entry);
    familyIds.set(name, Math.min(familyIds.get(name) ?? entry.typeId, entry.typeId));
  }
  const familyEntries = known.map(entry => ({
    ...entry,
    typeId: familyIds.get(familyName(entry))!,
    typeName: familyName(entry),
  }));
  const ranked = groupMining(familyEntries, 'material');
  // Colours follow the 30-day family rank as the window moves.
  const leaders = ranked.slice(0, top).map(group => ({ id: Number(group.id.slice('material-'.length)), name: group.name }));
  const leaderIds = new Set(leaders.map(leader => leader.id));
  const materials = ranked.length > top ? [...leaders, { id: OTHER_MATERIALS, name: 'Otros' }] : leaders;
  return {
    materials,
    unknownVolumeCount: inWindow.length - inWindow.filter(entry => entry.volume !== null).length,
    days: dates.map(date => {
      const onDay = familyEntries.filter(entry => entry.date === date);
      const segments = materials.flatMap(({ id, name }) => {
        const volumes = onDay.filter(entry => (leaderIds.has(entry.typeId) ? entry.typeId : OTHER_MATERIALS) === id).map(entry => entry.volume as number);
        return volumes.length ? [{ id, name, volume: decimalSum(volumes) }] : [];
      });
      return { date, segments, total: decimalSum(onDay.map(entry => entry.volume as number)) };
    }),
  };
}
